import { Stack, Grid, Typography, Icon, IconProps, Button } from '@ace-clublabs/shared-component-library';
import { MSSSpinOffStatus } from '@lib/types';
import { useHistory } from 'react-router-dom';
import { SpinOffMessage } from '../SpinOffMessage/SpinOffMessage';

export interface OverviewItemInfo {
  type: 'info';
  title: string | JSX.Element;
  subtitle?: string;
  isLoggedInMember?: boolean;
  email?: string;
  mobile?: string;
  home?: string;
  key: string;
  sx?: any;
  spinOffStatus?: MSSSpinOffStatus;
  mask?: 'title' | 'subtitle';
}

export interface OverviewItemLink {
  type: 'link';
  text: string;
  link: string;
  key: string;
  onClick?: () => void;
}

export interface OverviewItemDescription {
  type: 'description';
  description: string | JSX.Element;
  key: string;
}

export interface OverviewItemNotification {
  type: 'notification';
  content: string;
  key: string;
}

export interface OverviewItemProps {
  // left side properties
  iconName: IconProps['iconName'];
  overviewText: string;
  // right side properties
  items: (OverviewItemInfo | OverviewItemLink | OverviewItemDescription | OverviewItemNotification)[] | [];
  id?: string;
}

export const MembershipOverviewItem = (props: OverviewItemProps) => {
  const { iconName, overviewText, items } = props;
  const history = useHistory();

  const navigate = (link: string) => {
    if (link.includes('http')) {
      window.location.assign(link);
    } else {
      history.push(link);
    }
  };

  const onLinkClick = (item: OverviewItemLink) => {
    if (item.onClick) {
      item.onClick();
    } else {
      navigate(item.link);
    }
  };

  const rightSideItems = items.map((item, i) => {
    switch (item.type) {
      case 'info':
        const showSpinOffStatusComponent =
          item.spinOffStatus && item.spinOffStatus in MSSSpinOffStatus && item.spinOffStatus !== MSSSpinOffStatus.NONE;
        return (
          <Stack gap="4px" key={item.key} sx={{ ...(item.sx || {}) }}>
            <Typography
              variant="b1Regular"
              id={`${item.key}-title`}
              data-qm-block={item?.mask === 'title' ? 'true' : 'false'}
            >
              {item.title}
            </Typography>
            <Typography
              variant="b2Regular"
              sx={{ color: '#707070' }}
              id={item.key}
              data-qm-block={item?.mask === 'subtitle' ? 'true' : 'false'}
            >
              {item.subtitle}
            </Typography>
            {item.isLoggedInMember && (
              <>
                {item.email && (
                  <Typography
                    variant="b2Regular"
                    sx={{ color: '#707070' }}
                    id={`${item.key}-email`}
                    data-qm-encrypt="true"
                  >
                    Email: {item.email}
                  </Typography>
                )}
                {item.mobile && (
                  <Typography
                    variant="b2Regular"
                    sx={{ color: '#707070' }}
                    id={`${item.key}-mobile`}
                    data-qm-block="true"
                  >
                    Mobile: {item.mobile}
                  </Typography>
                )}
                {item.home && (
                  <Typography
                    variant="b2Regular"
                    sx={{ color: '#707070' }}
                    id={`${item.key}-home`}
                    data-qm-block="true"
                  >
                    Home: {item.home}
                  </Typography>
                )}
              </>
            )}
            {showSpinOffStatusComponent && (
              <Typography variant="b2Regular" sx={{ color: '#A24531' }} id={`member-spin-off-message`}>
                <SpinOffMessage spinOffStatus={item.spinOffStatus} />
              </Typography>
            )}
          </Stack>
        );
      case 'link':
        return (
          <Button
            variant="text"
            id={item.key}
            key={item.key}
            onClick={() => onLinkClick(item)}
            label={
              <Typography variant="b2Medium" sx={{ color: '#0C72DF' }}>
                {item.text}
              </Typography>
            }
            sx={{
              width: 'fit-content',
              minWidth: 'min-content',
              padding: '0',
              height: '2.5rem',
              ':hover': { background: 'none !important' }, // REMOVE IF NEEDED
            }}
            endIcon={<Icon iconName="chevron-right-regular" sx={{ color: '#0C72DF' }} />}
          />
        );
      case 'description':
        return (
          <Stack gap="8px" key={item.key}>
            <Typography variant="b2Regular" id={item.key}>
              {item.description}
            </Typography>
          </Stack>
        );
      case 'notification':
        return item.content;
      default:
        return <></>;
    }
  });

  return (
    <>
      {items && items.length > 0 && (
        <div id={props.id}>
          <Grid container sx={{ padding: '16px' }} columnSpacing={{ xs: '1rem', sm: '1rem', md: '3rem', lg: '1rem' }}>
            {/** Left Column */}
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <div
                className="membership-field"
                style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}
              >
                <Icon
                  iconName={iconName as any}
                  color="secondary"
                  sx={{
                    fontSize: '1.5rem',
                  }}
                />
                <Typography variant="b1Bold" id={`${props.id}-overview-text`}>
                  {overviewText}
                </Typography>
              </div>
            </Grid>

            {/** Right Column */}
            <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
              <Stack
                gap="1rem"
                sx={{
                  '@media (max-width: 769px)': { marginLeft: '2rem', marginTop: '1rem' },
                }}
              >
                {rightSideItems}
              </Stack>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};
